import { useState, forwardRef, useImperativeHandle } from 'react';
import './style.scss';
import { DateTime } from 'luxon';
import { TranslationService } from '../../../../services/translation.service';
import { FuelType } from '../../models/fuel-type.enum';
import ValidationInfo from '../../../../validation/validation-info';
import SelectFormGroup from '../../../Shared/SelectFormGroup';
import CarTypeSelectorComponent from '../CarTypeSelector';
import NumberFieldFormGroup from '../../../Shared/NumberFieldFormGroup';
import RadioFormGroup from '../../../Shared/RadioFormGroup';
import { CarUser } from '../../models/car-user.enum';
import type { ICarInformation } from '../../models/car-information.model';
import type IValidatable from '../../../Shared/validatable.interface';
import ValidationEngine from '../../../../validation/validation-engine';
import type { IServerValidationError } from '../../../Shared/validatable.interface';
import { ValueDepreciationScenario } from '../../models/value-depreciation-scenario.enum';
import { CarModelConstants } from '../../constants/car-model.constants';
import { VehicleTaxCalculator } from '../../helpers/vehicle-tax.calculator';
import { VehicleTaxConstants } from '../../constants/vehicle-tax.constants';
import { SanitizerService } from '../../../../services/sanitizer.service';
import { AnalyticsService } from '../../../../services/analytics-service';
import { ConfigurationService } from '../../../../services/configuration.service';
import PercentSliderFormGroup from '../../../Shared/PercentSliderFormGroup';
import { CarInformationField } from '../../models/car-information-field.enum';

export interface ICarInformationState {
  value: ICarInformation;
  validationInfo: ValidationInfo;
}

interface CarInformationProps {
  state: ICarInformationState;
  onChange?: (value: ICarInformation, field: CarInformationField) => void;
  showDetailsInformation?: boolean;
  className?: string;
}

function generateModelYearOptions() {
  const currentYear = new Date().getFullYear();
  const years: number[] = Array.from({ length: currentYear - 1950 + 1 }, (_, i) => 1950 + i);

  const options = years.map((year) => ({
    value: year.toString(),
    text: year.toString(),
  }));

  return options.reverse();
}

function getFuelPriceSekPerUnit(fuelType: FuelType): number | undefined {
  switch (fuelType) {
    case FuelType.Electric: {
      const fuelPriceElectric = ConfigurationService.get('pricecalculatorpage.carinformation.fuelpriceelectric');
      if (!fuelPriceElectric) {
        throw new Error('Fuel price for electric cars is not configured.');
      }
      return Number(fuelPriceElectric.replace(',', '.'));
    }
    case FuelType.Petrol: {
      const fuelPricePetrol = ConfigurationService.get('pricecalculatorpage.carinformation.fuelpricepetrol');
      if (!fuelPricePetrol) {
        throw new Error('Fuel price for petrol cars is not configured.');
      }
      return Number(fuelPricePetrol.replace(',', '.'));
    }
    default: {
      const fuelPriceDiesel = ConfigurationService.get('pricecalculatorpage.carinformation.fuelpricediesel');
      if (!fuelPriceDiesel) {
        throw new Error('Fuel price for diesel cars is not configured.');
      }
      return Number(fuelPriceDiesel.replace(',', '.'));
    }
  }
}

export default forwardRef<IValidatable, CarInformationProps>(function CarInformation({ state, onChange, showDetailsInformation = false, className = undefined }, ref) {
  // === STATE ===
  const [showDepreciationPercentageSlider, setShowDepreciationPercentageSlider] = useState(false);

  // === HELPER FUNCTIONS ===
  useImperativeHandle(ref, () => ({
    checkValidity(): ValidationInfo {
      const carTypeValidationInfo = new ValidationInfo(`${TranslationService.get('pricecalculatorpage.carinformation.cartype')} - `);
      const fuelTypeValidationInfo = new ValidationInfo(`${TranslationService.get('pricecalculatorpage.fueltype')} - `);
      const modelYearValidationInfo = new ValidationInfo(`${TranslationService.get('pricecalculatorpage.modelyear')} - `);
      const leasingCostPerMonthValidationInfo = new ValidationInfo(`${TranslationService.get('pricecalculatorpage.leasingcost')} - `);
      const fuelPriceSekPerUnitValidationInfo = new ValidationInfo(
        `${
          state.value.fuelType === FuelType.Electric
            ? TranslationService.get('pricecalculatorpage.carinformation.electricypricesekperkwh')
            : TranslationService.get('pricecalculatorpage.carinformation.fuelpricesekperliter')
        } - `
      );
      const fuelConsumptionUnitPerKmValidationInfo = new ValidationInfo(
        `${
          state.value.fuelType === FuelType.Electric
            ? TranslationService.get('pricecalculatorpage.carinformation.energyconsumptionkwhperkm')
            : TranslationService.get('pricecalculatorpage.carinformation.fuelconsumptionlitersperkm')
        } - `
      );
      const drivingDistancePerYearKmValidationInfo = new ValidationInfo(`${TranslationService.get('pricecalculatorpage.carinformation.drivingdistanceperyearinkm')} - `);
      const newCarPriceSekValidationInfo = new ValidationInfo(`${TranslationService.get('pricecalculatorpage.carinformation.newcarpricesek')} - `);
      const carLoanPerMonthValidationInfo = new ValidationInfo(`${TranslationService.get('pricecalculatorpage.carinformation.carloanpermonth')} - `);
      const vehicleTaxPerYearValidationInfo = new ValidationInfo(`${TranslationService.get('pricecalculatorpage.carinformation.vehicletaxperyear')} - `);
      const insuranceCostPerYearValidationInfo = new ValidationInfo(`${TranslationService.get('pricecalculatorpage.carinformation.insurancecostperyear')} - `);
      const serviceAndRepairCostPerYearValidationInfo = new ValidationInfo(`${TranslationService.get('pricecalculatorpage.carinformation.serviceandrepaircostperyear')} - `);
      const variableDepreciationPercentageValidationInfo = new ValidationInfo(`${TranslationService.get('pricecalculatorpage.carinformation.estimatedcarvaluedepricationpercentage')} - `);

      ValidationEngine.for(carTypeValidationInfo).requiredIf(true, state.value.carType, 'pricecalculatorpage.anyrequired').validate();
      ValidationEngine.for(fuelTypeValidationInfo).requiredIf(true, state.value.fuelType, 'pricecalculatorpage.anyrequired').validate();
      ValidationEngine.for(modelYearValidationInfo)
        .requiredIf(state.value.carUser === CarUser.CarOwner, state.value.modelYear, 'pricecalculatorpage.anyrequired')
        .validate();
      ValidationEngine.for(leasingCostPerMonthValidationInfo)
        .requiredIf(state.value.carUser === CarUser.CarLeaser, state.value.leasingCostPerMonth, 'pricecalculatorpage.anyrequired')
        .validate();
      ValidationEngine.for(fuelPriceSekPerUnitValidationInfo)
        .greaterThanOrEqual(state.value.fuelPriceSekPerUnit ?? 0, 0, 'pricecalculatorpage.valuegreaterorequaltozeroerror')
        .validate();
      ValidationEngine.for(fuelConsumptionUnitPerKmValidationInfo)
        .greaterThanOrEqual(state.value.fuelConsumptionUnitPerKm ?? 0, 0, 'pricecalculatorpage.valuegreaterorequaltozeroerror')
        .validate();
      ValidationEngine.for(drivingDistancePerYearKmValidationInfo)
        .greaterThanOrEqual(state.value.drivingDistancePerYearKm ?? 0, 0, 'pricecalculatorpage.valuegreaterorequaltozeroerror')
        .validate();
      ValidationEngine.for(newCarPriceSekValidationInfo)
        .greaterThanOrEqual(state.value.newCarPriceSek ?? 0, 0, 'pricecalculatorpage.valuegreaterorequaltozeroerror')
        .validate();
      ValidationEngine.for(carLoanPerMonthValidationInfo)
        .greaterThanOrEqual(state.value.carLoanPerMonth ?? 0, 0, 'pricecalculatorpage.valuegreaterorequaltozeroerror')
        .validate();
      ValidationEngine.for(vehicleTaxPerYearValidationInfo)
        .greaterThanOrEqual(state.value.vehicleTaxPerYear ?? 0, 0, 'pricecalculatorpage.valuegreaterorequaltozeroerror')
        .validate();
      ValidationEngine.for(insuranceCostPerYearValidationInfo)
        .greaterThanOrEqual(state.value.insuranceCostPerYear ?? 0, 0, 'pricecalculatorpage.valuegreaterorequaltozeroerror')
        .validate();
      ValidationEngine.for(serviceAndRepairCostPerYearValidationInfo)
        .greaterThanOrEqual(state.value.serviceAndRepairCostPerYear ?? 0, 0, 'pricecalculatorpage.valuegreaterorequaltozeroerror')
        .validate();
      ValidationEngine.for(variableDepreciationPercentageValidationInfo)
        .greaterThanOrEqual(state.value.variableDepreciationPercentage ?? -1, 0, 'pricecalculatorpage.valuegreaterorequaltozeroerror')
        .lessThan(state.value.variableDepreciationPercentage ?? 0, 101, 'pricecalculatorpage.valuemaxonehundrederror')
        .requiredIf(state.value.carUser === CarUser.CarOwner, state.value.variableDepreciationPercentage, 'pricecalculatorpage.anyrequired')
        .validate();

      return new ValidationInfo('', [], {
        carType: carTypeValidationInfo,
        fuelType: fuelTypeValidationInfo,
        modelYear: modelYearValidationInfo,
        leasingCostPerMonth: leasingCostPerMonthValidationInfo,
        fuelPriceSekPerUnit: fuelPriceSekPerUnitValidationInfo,
        fuelConsumptionUnitPerKm: fuelConsumptionUnitPerKmValidationInfo,
        drivingDistancePerYearKm: drivingDistancePerYearKmValidationInfo,
        newCarPriceSek: newCarPriceSekValidationInfo,
        carLoanPerMonth: carLoanPerMonthValidationInfo,
        vehicleTaxPerYear: vehicleTaxPerYearValidationInfo,
        insuranceCostPerYear: insuranceCostPerYearValidationInfo,
        serviceAndRepairCostPerYear: serviceAndRepairCostPerYearValidationInfo,
        variableDepreciationPercentage: variableDepreciationPercentageValidationInfo,
      });
    },
    handleServerValidationErrors(errors: IServerValidationError[]): ValidationInfo {
      return new ValidationInfo(
        `Car information component - `,
        errors
          .filter((e) => e.name === undefined)
          .map((e) => e.errorMessages)
          .reduce((a, b) => a.concat(b), []),
        {}
      );
    },
  }));

  return (
    <div className={`car-information ${className ?? ''}`}>
      <fieldset>
        <legend className="visually-hidden">
          {TranslationService.get('pricecalculatorpage.carinformationheading')}, {TranslationService.get('common.formsrequiredfieldsmessage')}
        </legend>
        <h2 className="mb-1">{TranslationService.get('pricecalculatorpage.carinformationheading')}</h2>
        <p>{TranslationService.get('common.formsrequiredfieldsmessage')}</p>
        <RadioFormGroup
          id="car-user"
          labelText={TranslationService.get('pricecalculatorpage.caruser')}
          rowLayout={true}
          rowLayoutSmUp={true}
          options={[
            { value: CarUser.CarOwner, text: TranslationService.get('pricecalculatorpage.carowner') },
            { value: CarUser.CarLeaser, text: TranslationService.get('pricecalculatorpage.carleaser') },
          ]}
          state={{ value: state.value.carUser, validationInfo: state.validationInfo.children.carUser ?? new ValidationInfo() }}
          onChange={(value) => {
            // NV-5959: Tracking performed when going to the next step or updating comparison since a default value is set
            // see https://github.com/devtrafik/web-vasttrafikse/pull/950#discussion_r1620764133
            onChange?.(
              {
                ...state.value,
                carUser: value as CarUser,
                modelYear: DateTime.now().year.toString(),
                vehicleTaxPerYear:
                  state.value.fuelType && state.value.carType
                    ? VehicleTaxCalculator.calculateCost(
                        {
                          currentDate: DateTime.now(),
                          registrationDate: DateTime.now().set({ year: parseInt(state.value.modelYear!, 10) }),
                          fuelType: state.value.fuelType,
                          emissionsCo2PerKm: CarModelConstants.find((model) => model.type === state.value.carType && model.fuelType === state.value.fuelType)?.emissionsCo2PerKm ?? 0,
                          weightKg: CarModelConstants.find((model) => model.type === state.value.carType && model.fuelType === state.value.fuelType)?.weightKg,
                        },
                        VehicleTaxConstants
                      ).costInSek
                    : undefined,
              },
              CarInformationField.CarUser
            );
          }}
        />

        <CarTypeSelectorComponent
          id={'car-type-selector'}
          labelText={TranslationService.get('pricecalculatorpage.carinformation.cartype')}
          state={{ value: state.value.carType, validationInfo: state.validationInfo.children.carType ?? new ValidationInfo() }}
          onChange={(value) =>
            onChange?.(
              {
                ...state.value,
                carType: value,
                newCarPriceSek: CarModelConstants.find((model) => model.type === value && model.fuelType === state.value.fuelType)?.priceInSek,
                fuelConsumptionUnitPerKm: CarModelConstants.find((model) => model.type === value && model.fuelType === state.value.fuelType)?.fuelConsumptionPer10Km,
                vehicleTaxPerYear:
                  state.value.modelYear && state.value.fuelType
                    ? VehicleTaxCalculator.calculateCost(
                        {
                          currentDate: DateTime.now(),
                          registrationDate: DateTime.now().set({ year: parseInt(state.value.modelYear, 10) }),
                          fuelType: state.value.fuelType,
                          emissionsCo2PerKm: CarModelConstants.find((model) => model.type === value && model.fuelType === state.value.fuelType)?.emissionsCo2PerKm ?? 0,
                          weightKg: CarModelConstants.find((model) => model.type === value && model.fuelType === state.value.fuelType)?.weightKg,
                        },
                        VehicleTaxConstants
                      ).costInSek
                    : undefined,
              },
              CarInformationField.CarType
            )
          }
        />

        {state.value.carUser === CarUser.CarOwner && (
          <SelectFormGroup
            id="car-model-year"
            className="mb-3"
            labelText={TranslationService.get('pricecalculatorpage.modelyear')}
            options={generateModelYearOptions()}
            placeholder={TranslationService.get('pricecalculatorpage.modelyearplaceholder')}
            required={true}
            state={{ value: state.value.modelYear, validationInfo: state.validationInfo.children.modelYear ?? new ValidationInfo() }}
            onChange={(value) =>
              onChange?.(
                {
                  ...state.value,
                  modelYear: value,
                  vehicleTaxPerYear:
                    state.value.carType && state.value.fuelType
                      ? VehicleTaxCalculator.calculateCost(
                          {
                            currentDate: DateTime.now(),
                            registrationDate: DateTime.now().set({ year: parseInt(value, 10) }),
                            fuelType: state.value.fuelType,
                            emissionsCo2PerKm: CarModelConstants.find((model) => model.type === state.value.carType && model.fuelType === state.value.fuelType)?.emissionsCo2PerKm ?? 0,
                            weightKg: CarModelConstants.find((model) => model.type === state.value.carType && model.fuelType === state.value.fuelType)?.weightKg,
                          },
                          VehicleTaxConstants
                        ).costInSek
                      : undefined,
                },
                CarInformationField.ModelYear
              )
            }
          />
        )}

        <div className={`${showDetailsInformation ? 'row' : ''}`}>
          <SelectFormGroup
            id={'fuel-type'}
            className={`mb-3 ${showDetailsInformation ? 'col-md-6' : ''}`}
            labelText={TranslationService.get('pricecalculatorpage.fueltype')}
            placeholder={TranslationService.get('pricecalculatorpage.fueltype.placeholder')}
            required={true}
            options={[
              { value: FuelType.Petrol, text: TranslationService.get('pricecalculatorpage.fueltype.petrol') },
              { value: FuelType.Diesel, text: TranslationService.get('pricecalculatorpage.fueltype.diesel') },
              { value: FuelType.Electric, text: TranslationService.get('pricecalculatorpage.fueltype.electric') },
            ]}
            state={{ value: state.value.fuelType, validationInfo: state.validationInfo.children.fuelType ?? new ValidationInfo() }}
            onChange={(value) => {
              AnalyticsService.addEvent('Priskalkylator', 'Val av bränsletyp', value);
              onChange?.(
                {
                  ...state.value,
                  fuelType: value as FuelType,
                  fuelPriceSekPerUnit: getFuelPriceSekPerUnit(value as FuelType),
                  newCarPriceSek: CarModelConstants.find((model) => model.type === state.value.carType && model.fuelType === value)?.priceInSek,
                  fuelConsumptionUnitPerKm: CarModelConstants.find((model) => model.type === state.value.carType && model.fuelType === value)?.fuelConsumptionPer10Km,
                  vehicleTaxPerYear:
                    state.value.carType && state.value.modelYear
                      ? VehicleTaxCalculator.calculateCost(
                          {
                            currentDate: DateTime.now(),
                            registrationDate: DateTime.now().set({ year: parseInt(state.value.modelYear, 10) }),
                            fuelType: value as FuelType,
                            emissionsCo2PerKm: CarModelConstants.find((model) => model.type === state.value.carType && model.fuelType === value)?.emissionsCo2PerKm ?? 0,
                            weightKg: CarModelConstants.find((model) => model.type === state.value.carType && model.fuelType === value)?.weightKg,
                          },
                          VehicleTaxConstants
                        ).costInSek
                      : undefined,
                },
                CarInformationField.FuelType
              );
            }}
          />
          {showDetailsInformation && (
            <div className="details col-md-6">
              <NumberFieldFormGroup
                inputId={'fuel-price-sek-per-unit'}
                required={false}
                allowDecimals={true}
                labelText={
                  state.value.fuelType === FuelType.Electric
                    ? TranslationService.get('pricecalculatorpage.carinformation.electricypricesekperkwh')
                    : TranslationService.get('pricecalculatorpage.carinformation.fuelpricesekperliter')
                }
                ariaDescribedby="fuel-price-information-help-text"
                min={0}
                max={30}
                state={{ value: state.value.fuelPriceSekPerUnit, validationInfo: state.validationInfo.children.fuelPriceSekPerUnit ?? new ValidationInfo() }}
                onChange={(value) => onChange?.({ ...state.value, fuelPriceSekPerUnit: value }, CarInformationField.FuelPriceSekPerUnit)}
                helpTextElement={
                  <div
                    id="fuel-price-information-help-text"
                    className="mt-1"
                    dangerouslySetInnerHTML={{ __html: SanitizerService.sanitizeHtml(TranslationService.get('pricecalculatorpage.carinformation.fuelpriceinformation')) }}
                  />
                }
              />
            </div>
          )}
        </div>
        <NumberFieldFormGroup
          inputId={'driving-distance-per-year-in-km'}
          required={false}
          labelText={TranslationService.get('pricecalculatorpage.carinformation.drivingdistanceperyearinkm')}
          ariaDescribedby="driving-distance-information-help-text"
          min={0}
          state={{ value: state.value.drivingDistancePerYearKm, validationInfo: state.validationInfo.children.drivingDistancePerYearKm ?? new ValidationInfo() }}
          onChange={(value) => {
            let estimatedCarValueDepreciation: ValueDepreciationScenario;
            if (value === undefined) {
              estimatedCarValueDepreciation = ValueDepreciationScenario.Normal;
            } else if (value < 9000) {
              estimatedCarValueDepreciation = ValueDepreciationScenario.Low;
            } else if (value < 12000) {
              estimatedCarValueDepreciation = ValueDepreciationScenario.Normal;
            } else {
              estimatedCarValueDepreciation = ValueDepreciationScenario.High;
            }
            onChange?.(
              {
                ...state.value,
                drivingDistancePerYearKm: value,
                estimatedCarValueDeprication: estimatedCarValueDepreciation,
              },
              CarInformationField.DrivingDistancePerYearKm
            );
          }}
          helpTextElement={
            <div
              id="driving-distance-information-help-text"
              dangerouslySetInnerHTML={{ __html: SanitizerService.sanitizeHtml(TranslationService.get('pricecalculatorpage.carinformation.drivingdistanceinformation')) }}
            ></div>
          }
        />
        {state.value.carUser === CarUser.CarOwner && !showDetailsInformation && (
          <div id="depreciation-heading" className="mb-2">
            <strong>{TranslationService.get('pricecalculatorpage.carinformation.depreciation')}</strong>
          </div>
        )}
        {state.value.carUser === CarUser.CarOwner && !showDetailsInformation && (
          <div
            id="depreciation-commute-percentage-text"
            className="mb-2"
            dangerouslySetInnerHTML={{
              __html: SanitizerService.sanitizeHtml(
                TranslationService.get('pricecalculatorpage.carinformation.depreciationCommutePercentageText').replace(
                  '{depreciation-percentage}',
                  `<strong>${state.value.commutePercentage?.toString()}%</strong>`
                )
              ),
            }}
          ></div>
        )}
        {state.value.carUser === CarUser.CarOwner && !showDetailsInformation && !showDepreciationPercentageSlider && (
          <button className="btn--as-link" onClick={() => setShowDepreciationPercentageSlider(true)}>
            {TranslationService.get('pricecalculatorpage.carinformation.changeproportion')}
          </button>
        )}
        {state.value.carUser === CarUser.CarOwner && (showDetailsInformation || showDepreciationPercentageSlider) && (
          <PercentSliderFormGroup
            id="variable-depreciation-percentage"
            className="mb-0"
            labelText={TranslationService.get('pricecalculatorpage.carinformation.estimatedcarvaluedepricationpercentage')}
            ariaDescribedby="variable-depreciation-percentage-helptext"
            value={state.value.variableDepreciationPercentage ?? 0}
            state={{ value: state.value.variableDepreciationPercentage ?? 0, validationInfo: state.validationInfo.children.variableDepreciationPercentage ?? new ValidationInfo() }}
            onChange={(value) => onChange?.({ ...state.value, variableDepreciationPercentage: value }, CarInformationField.VariableDepreciationPercentage)}
          />
        )}
        {state.value.carUser === CarUser.CarOwner && showDetailsInformation && (
          <div
            id="depreciation-commute-percentage-text-under"
            className="mb-3 form-text"
            dangerouslySetInnerHTML={{
              __html: SanitizerService.sanitizeHtml(
                TranslationService.get('pricecalculatorpage.carinformation.depreciationCommutePercentageText').replace(
                  '{depreciation-percentage}',
                  `<strong>${state.value.commutePercentage?.toString()}%</strong>`
                )
              ),
            }}
          ></div>
        )}
        {state.value.carUser === CarUser.CarLeaser && (
          <NumberFieldFormGroup
            inputId={'Leasing-cost-per-month'}
            required={true}
            placeholder={TranslationService.get('pricecalculatorpage.leasingcostplaceholder')}
            labelText={TranslationService.get('pricecalculatorpage.leasingcost')}
            min={0}
            max={50000}
            className={!showDetailsInformation ? 'mb-0' : ''}
            state={{ value: state.value.leasingCostPerMonth, validationInfo: state.validationInfo.children.leasingCostPerMonth ?? new ValidationInfo() }}
            onChange={(value) => onChange?.({ ...state.value, leasingCostPerMonth: value }, CarInformationField.LeasingCostPerMonth)}
          />
        )}
        {/* ------------Only for details view------------ */}
        {showDetailsInformation && (
          <div className="details last-child-margin-bottom-none">
            <NumberFieldFormGroup
              inputId={'fuel-consumption-units-per-km'}
              required={false}
              allowDecimals={true}
              labelText={
                state.value.fuelType === FuelType.Electric
                  ? TranslationService.get('pricecalculatorpage.carinformation.energyconsumptionkwhperkm')
                  : TranslationService.get('pricecalculatorpage.carinformation.fuelconsumptionlitersperkm')
              }
              ariaDescribedby="fuel-consumption-informaiton-help-text"
              min={0}
              max={20}
              step={0.01}
              state={{ value: state.value.fuelConsumptionUnitPerKm, validationInfo: state.validationInfo.children.fuelConsumptionUnitPerKm ?? new ValidationInfo() }}
              onChange={(value) => onChange?.({ ...state.value, fuelConsumptionUnitPerKm: value }, CarInformationField.FuelConsumptionUnitPerKm)}
              helpTextElement={
                <div
                  id="fuel-consumption-informaiton-help-text"
                  dangerouslySetInnerHTML={{ __html: SanitizerService.sanitizeHtml(TranslationService.get('pricecalculatorpage.carinformation.fuelconsumptioninformation')) }}
                ></div>
              }
            />
            {state.value.carUser === CarUser.CarOwner && (
              <NumberFieldFormGroup
                inputId={'new-car-price-sek'}
                required={true}
                labelText={TranslationService.get('pricecalculatorpage.carinformation.newcarpricesek')}
                min={0}
                state={{ value: state.value.newCarPriceSek, validationInfo: state.validationInfo.children.newCarPriceSek ?? new ValidationInfo() }}
                onChange={(value) => onChange?.({ ...state.value, newCarPriceSek: value }, CarInformationField.NewCarPriceSek)}
              />
            )}
            {state.value.carUser === CarUser.CarOwner && (
              <NumberFieldFormGroup
                inputId={'car-loan-per-month'}
                required={false}
                labelText={TranslationService.get('pricecalculatorpage.carinformation.carloanpermonth')}
                min={0}
                state={{ value: state.value.carLoanPerMonth, validationInfo: state.validationInfo.children.carLoanPerMonth ?? new ValidationInfo() }}
                onChange={(value) => onChange?.({ ...state.value, carLoanPerMonth: value }, CarInformationField.CarLoanPerMonth)}
              />
            )}
            <NumberFieldFormGroup
              inputId={'vehicle-tax-per-year'}
              required={false}
              labelText={TranslationService.get('pricecalculatorpage.carinformation.vehicletaxperyear')}
              ariaDescribedby="vehicle-tax-information-help-text"
              min={0}
              state={{ value: state.value.vehicleTaxPerYear, validationInfo: state.validationInfo.children.vehicleTaxPerYear ?? new ValidationInfo() }}
              onChange={(value) => onChange?.({ ...state.value, vehicleTaxPerYear: value }, CarInformationField.VehicleTaxPerYear)}
              helpTextElement={
                <div
                  id="vehicle-tax-information-help-text"
                  dangerouslySetInnerHTML={{ __html: SanitizerService.sanitizeHtml(TranslationService.get('pricecalculatorpage.carinformation.vehicletaxinformation')) }}
                ></div>
              }
            />
            <NumberFieldFormGroup
              inputId={'insurance-cost-per-year'}
              required={false}
              labelText={TranslationService.get('pricecalculatorpage.carinformation.insurancecostperyear')}
              ariaDescribedby="insurance-cost-information-help-text"
              min={0}
              state={{ value: state.value.insuranceCostPerYear, validationInfo: state.validationInfo.children.insuranceCostPerYear ?? new ValidationInfo() }}
              onChange={(value) => onChange?.({ ...state.value, insuranceCostPerYear: value }, CarInformationField.InsuranceCostPerYear)}
              helpTextElement={
                <div
                  id="insurance-cost-information-help-text"
                  dangerouslySetInnerHTML={{ __html: SanitizerService.sanitizeHtml(TranslationService.get('pricecalculatorpage.carinformation.insurancecostinformation')) }}
                ></div>
              }
            />
            <NumberFieldFormGroup
              inputId={'service-and-repair-cost-per-year'}
              required={false}
              labelText={TranslationService.get('pricecalculatorpage.carinformation.serviceandrepaircostperyear')}
              ariaDescribedby="service-and-repair-cost-information-help-text"
              min={0}
              state={{ value: state.value.serviceAndRepairCostPerYear, validationInfo: state.validationInfo.children.serviceAndRepairCostPerYear ?? new ValidationInfo() }}
              onChange={(value) => onChange?.({ ...state.value, serviceAndRepairCostPerYear: value }, CarInformationField.ServiceAndRepairCostPerYear)}
              helpTextElement={
                <div
                  id="service-and-repair-cost-information-help-text"
                  dangerouslySetInnerHTML={{ __html: SanitizerService.sanitizeHtml(TranslationService.get('pricecalculatorpage.carinformation.serviceanrepaircostinformation')) }}
                ></div>
              }
            />
          </div>
        )}
      </fieldset>
    </div>
  );
});
